import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/core';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { CapabilityDTO } from '@atlassiansox/phobos-client-ts';

import { apis, apiResolve } from '@/services';
import {
  useFetchErrorController,
  CreateEntityContext,
  Creatable,
  useNavigationController,
} from '@/controllers';
import { ActionButtons, HelpDialog, SkeletonBlock, PageHeader } from '@/components/primitive';
import { CapabilityStatsInfo, CapabilityMetaInfo } from '@/components/composed';
import {
  grid,
  positionRelative,
  spaceTopRelaxed,
  marginAuto,
  marginZero,
  flexOne,
  SPACE_COMFORTABLE,
  flexCenter,
  VIEWING_WIDTH,
} from '@/styles';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import { FetchError } from '@/services/api-service/api-service';
import { fetchSlosSpec, prefetchSpec } from '@/services/api-service/api-queries';

import { capabilityHelperCopy } from './copy';
import PageContent from './content';
import { CapabilityViewProps } from './types';
import { CapabilityTabNames } from './constants';

const HEADER_SPACING = spaceTopRelaxed;

const pageTitleRowStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

/** lifted this out so the skeleton renders in place with the actual content */
const pageLayoutStyles = [
  positionRelative,
  marginAuto,
  css`
    max-width: ${VIEWING_WIDTH}px;
  `,
];

const headerBlockStyles = css`
  display: flex;
  align-items: flex-start;
`;

function prepareCreateFormForPrefill(
  dispatch: (arg: Creatable) => void,
  createEntityState: Creatable,
  data: CapabilityDTO
) {
  dispatch({
    ...createEntityState,
    type: 'slo',
    capability:
      data?.name && data?.publicId
        ? {
            label: data?.name,
            publicId: data?.publicId,
            value: data?.publicId,
          }
        : undefined,
  });
}

function rollbackCreateFormPrefillState(dispatch: (arg: Creatable) => void, editState: Creatable) {
  return () => dispatch(editState);
}

// Prefetch some queries used by child components
function prefetchData(queryClient: QueryClient, id: string) {
  prefetchSpec(queryClient, 'capability.{id}.events.summary', { id });
  prefetchSpec(queryClient, 'hot.search.{id}', { id });

  const { queryKey: fetchSlosKey, fetchSlos } = fetchSlosSpec(id);
  queryClient.prefetchInfiniteQuery(fetchSlosKey, fetchSlos);
}

const CapabilityView: React.FC<CapabilityViewProps> = ({ tabName }) => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ?? '';

  const [isPrefetched, setIsPrefetched] = React.useState<boolean>(false);

  //  Execute prefetch
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isPrefetched || tabName === CapabilityTabNames.HISTORY) return;
    setIsPrefetched(true);
    prefetchData(queryClient, id);
  }, [id, queryClient, isPrefetched, tabName]);

  const { onError } = useFetchErrorController();
  const { data, isInitialLoading: isLoading, error } = useQuery(
    ['capability', id],
    () => apiResolve(apis.capability.getCapabilityByPublicId({ publicId: id })),
    { onError }
  );

  const createControllerRef = useRef(React.useContext(CreateEntityContext)); // using a ref so we don't want to specify it as a dep in the useEffect
  useEffect(() => {
    if (data) {
      const { dispatch, ...createEntityState } = createControllerRef.current;
      prepareCreateFormForPrefill(dispatch, createEntityState, data);
      return rollbackCreateFormPrefillState(dispatch, createEntityState);
    }
    return undefined;
  }, [data]);

  const { onNavigation } = useNavigationController();
  useEffect(() => {
    if (error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading || (!data && !error) || data === undefined) {
    return (
      <div css={pageLayoutStyles}>
        <div
          css={[
            grid,
            css`
              grid-template-columns: 5fr 1fr;
            `,
          ]}
        >
          <SkeletonBlock />
          <SkeletonBlock />
        </div>
        <div css={HEADER_SPACING}>
          <PageHeader title="" isLoading={isLoading} />
        </div>
      </div>
    );
  }

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="Capability">
        <div data-testid="capability-focus-container" css={pageLayoutStyles}>
          <div
            css={css`
              margin-bottom: ${SPACE_COMFORTABLE}px;
            `}
          >
            <div css={pageTitleRowStyles}>
              <PageHeader title={data.name} description={data.description} css={flexOne} />
              <div css={flexCenter}>
                <HelpDialog withInterest>{capabilityHelperCopy}</HelpDialog>
                <ActionButtons id={id} entityName={data.name} type="capability" />
              </div>
            </div>
            <div css={headerBlockStyles}>
              <CapabilityMetaInfo
                css={marginZero}
                owner={data.owner}
                product={data.product}
                team={data.team}
                importance={data.importance}
              />

              <CapabilityStatsInfo
                publicId={data.publicId}
                breaches={data.breaches}
                downstreamDependencies={data.downstreamDependencies}
                upstreamDependencies={data.upstreamDependencies}
              />
            </div>
          </div>
          <PageContent id={id} tabName={tabName} />
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

export default CapabilityView;
